/**
 * Budget Store
 * Zustand store for managing budget state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface BudgetCategory {
  id: string;
  name: string;
  nameEn: string;
  percentage: number;
  color: string;
  icon: string;
}

export interface Expense {
  id: string;
  categoryId: string;
  amount: number;
  description: string;
  date: string;
}

interface ServerCategory {
  id: string;
  name: string;
  nameKey: string;
  color: string;
  percentage: number;
}

interface CategoryPercentageUpdate {
  id: string;
  percentage: number;
}

interface BudgetState {
  // Total budget to distribute
  totalBudget: number;
  setTotalBudget: (amount: number) => void;

  // Budget categories with percentages
  categories: BudgetCategory[];
  updateCategoryPercentage: (id: string, percentage: number) => void;
  updateAllCategoryPercentages: (updates: CategoryPercentageUpdate[]) => void;
  syncFromServer: (serverCategories: ServerCategory[]) => void;

  // Expenses
  expenses: Expense[];
  addExpense: (expense: Omit<Expense, 'id'>) => void;
  removeExpense: (id: string) => void;

  // Computed values
  getCategoryAmount: (categoryId: string) => number;
  getCategorySpent: (categoryId: string) => number;
  getCategoryRemaining: (categoryId: string) => number;
  getMonthlyExpenses: (year: number, month: number) => Expense[];
}

const DEFAULT_CATEGORIES: BudgetCategory[] = [
  { id: 'custo-fixo', name: 'Custo Fixo', nameEn: 'Fixed Costs', percentage: 30, color: '#f97316', icon: 'building' },
  { id: 'liberdade', name: 'Liberdade Financeira', nameEn: 'Financial Freedom', percentage: 25, color: '#a855f7', icon: 'dollar' },
  { id: 'conforto', name: 'Conforto', nameEn: 'Comfort', percentage: 15, color: '#3b82f6', icon: 'heart' },
  { id: 'metas', name: 'Metas', nameEn: 'Goals', percentage: 10, color: '#22c55e', icon: 'target' },
  { id: 'prazeres', name: 'Prazeres', nameEn: 'Pleasures', percentage: 15, color: '#eab308', icon: 'smile' },
  { id: 'conhecimento', name: 'Conhecimento', nameEn: 'Knowledge', percentage: 5, color: '#06b6d4', icon: 'book' },
];

export const useBudgetStore = create<BudgetState>()(
  persist(
    (set, get) => ({
      totalBudget: 1000,
      categories: DEFAULT_CATEGORIES,
      expenses: [],

      setTotalBudget: (amount) => set({ totalBudget: amount }),

      updateCategoryPercentage: (id, percentage) =>
        set((state) => ({
          categories: state.categories.map((cat) =>
            cat.id === id ? { ...cat, percentage } : cat
          ),
        })),

      updateAllCategoryPercentages: (updates) =>
        set((state) => ({
          categories: state.categories.map((cat) => {
            const update = updates.find((u) => u.id === cat.id);
            return update ? { ...cat, percentage: update.percentage } : cat;
          }),
        })),

      syncFromServer: (serverCategories) =>
        set((state) => {
          // Map server categories to local categories, preserving icons
          const iconMap: Record<string, string> = {
            fixed_costs: 'building',
            financial_freedom: 'dollar',
            comfort: 'heart',
            goals: 'target',
            pleasures: 'smile',
            knowledge: 'book',
          };

          const nameEnMap: Record<string, string> = {
            fixed_costs: 'Fixed Costs',
            financial_freedom: 'Financial Freedom',
            comfort: 'Comfort',
            goals: 'Goals',
            pleasures: 'Pleasures',
            knowledge: 'Knowledge',
          };

          const updatedCategories = serverCategories.map((sc) => ({
            id: sc.nameKey,
            name: sc.name,
            nameEn: nameEnMap[sc.nameKey] || sc.name,
            percentage: sc.percentage,
            color: sc.color,
            icon: iconMap[sc.nameKey] || 'circle',
          }));

          // Merge with defaults if server has fewer categories
          if (updatedCategories.length < state.categories.length) {
            const existingKeys = new Set(updatedCategories.map((c) => c.id));
            const missing = state.categories.filter((c) => !existingKeys.has(c.id));
            return { categories: [...updatedCategories, ...missing] };
          }

          return { categories: updatedCategories };
        }),

      addExpense: (expense) =>
        set((state) => ({
          expenses: [
            ...state.expenses,
            { ...expense, id: `expense_${Date.now()}` },
          ],
        })),

      removeExpense: (id) =>
        set((state) => ({
          expenses: state.expenses.filter((e) => e.id !== id),
        })),

      getCategoryAmount: (categoryId) => {
        const state = get();
        const category = state.categories.find((c) => c.id === categoryId);
        if (!category) return 0;
        return (state.totalBudget * category.percentage) / 100;
      },

      getCategorySpent: (categoryId) => {
        const state = get();
        const now = new Date();
        const monthExpenses = state.expenses.filter((e) => {
          const expenseDate = new Date(e.date);
          return (
            e.categoryId === categoryId &&
            expenseDate.getMonth() === now.getMonth() &&
            expenseDate.getFullYear() === now.getFullYear()
          );
        });
        return monthExpenses.reduce((sum, e) => sum + e.amount, 0);
      },

      getCategoryRemaining: (categoryId) => {
        const state = get();
        const amount = state.getCategoryAmount(categoryId);
        const spent = state.getCategorySpent(categoryId);
        return amount - spent;
      },

      getMonthlyExpenses: (year, month) => {
        const state = get();
        return state.expenses.filter((e) => {
          const expenseDate = new Date(e.date);
          return (
            expenseDate.getMonth() === month &&
            expenseDate.getFullYear() === year
          );
        });
      },
    }),
    {
      name: 'financeflow-budget',
    }
  )
);
